import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import Fab from '../Fab';
import { RC } from '../../utils/fonts';
import { useStore } from '../../store';
import WfShare from '../WfShare';

// Service Workflows (服务流程) — staff module.
// Two tabs: Instances (client workflows in flight) and Flows (the per-service step templates).
// Every service inherits the single global default flow until staff edit it (copy-on-write).
// Creating an instance = pick client + service → price auto-resolved → flow snapshotted → share link.

const G = '#0E7C66';
const ddl: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' };
const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 9, color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };
// 10.5 → 11.5px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
// Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like this:
// the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular weight, where
// they were dark grey in bold, 10px above and below on one 16px line, a 37px band, white where it was light grey.
const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 12.5, padding: '8px 10px', borderBottom: '1px solid var(--border)' };
const btn: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, border: 'none', background: G, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

const fmt = (n: any) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtTs = (ts: number) => { try { return new Date(ts * 1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const shareUrl = (token: string) => `${window.location.origin}/w/${token}`;

const PARTY_META: Record<string, { label: string; bg: string; c: string }> = {
  client: { label: 'CLIENT', bg: '#EFF6FF', c: '#1D4ED8' },
  agent: { label: 'AGENT', bg: '#FFF7ED', c: '#C2410C' },
  slf: { label: 'SLF', bg: '#ECFDF5', c: '#0E7C66' },
};
const PartyBadges = ({ parties }: { parties: string[] }) => (
  <span style={{ display: 'inline-flex', gap: 4 }}>
    {(parties || []).map(p => { const m = PARTY_META[p] || PARTY_META.slf; return <span key={p} style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', padding: '2px 7px', borderRadius: 5, background: m.bg, color: m.c }}>{m.label}</span>; })}
  </span>
);
const STATUS_META: Record<string, { l: string; bg: string; c: string }> = {
  waiting: { l: 'Waiting', bg: 'var(--c-gray-50)', c: 'var(--text-secondary)' },
  in_progress: { l: 'In Progress', bg: '#FEF9C3', c: '#A16207' },
  complete: { l: 'Complete', bg: '#ECFDF5', c: '#15803D' },
};
const StatusChip = ({ s }: { s: string }) => { const m = STATUS_META[s] || STATUS_META.waiting; return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: m.bg, color: m.c, whiteSpace: 'nowrap' }}>{m.l}</span>; };

const Spin = () => <i className="ti ti-loader-2" style={{ animation: 'spin .8s linear infinite', display: 'inline-block' }} />;
const chipS: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap' };
// /api/services and /api/services/companies answer with a BARE ARRAY, /api/clients with {rows}.
const asRows = (d: any): any[] => (Array.isArray(d) ? d : (d?.rows || []));

// Upload-slot ceiling — mirrors MAX_UPLOAD_SLOTS on the server, which is what actually enforces it.
const MAX_SLOTS = 50;
// The key addresses the uploaded file, so it must not repeat. Numbering by length alone collided as
// soon as a slot in the middle was deleted (10 slots, delete the 5th, add one → "slot-10" twice, and
// the same file then showed under both labels), so skip past whatever is already taken.
const nextSlotKey = (ups: any[]): string => {
  const used = new Set((ups || []).map((u: any) => String(u?.key || '')));
  for (let n = (ups || []).length + 1; ; n++) if (!used.has(`slot-${n}`)) return `slot-${n}`;
};
// The services company list is unfiltered (it includes portal-merchant and other non-firm rows, e.g.
// "PHB Thai Co., Ltd." with zero services). A workflow can only price a service, so only companies
// that actually carry a catalogue belong in the picker.
const firmsWithServices = (d: any): any[] => asRows(d).filter((c: any) => Number(c.service_count) > 0);

// ── Price summary card (quotation-style breakdown from the snapshot) ─────────
function PriceCard({ price, t, onEdit }: { price: any; t: any; onEdit?: () => void }) {
  if (!price || price.unitPrice == null) return null;
  const row = (l: string, v: string, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: strong ? 14 : 12.5, fontWeight: strong ? 800 : 500, padding: '3px 0' }}>
      <span style={{ color: strong ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{l}</span><span>{v}</span>
    </div>
  );
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', minWidth: 260 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={lbl}>{t('wf_price')} · {price.source === 'client_service' ? t('wf_price_cs') : price.source === 'manual' ? t('wf_price_manual') : t('wf_price_catalog')}</div>
        {onEdit && <button onClick={onEdit} style={{ border: 'none', background: 'none', color: G, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}><i className="ti ti-pencil" /> {t('edit')}</button>}
      </div>
      {row(`${price.name || ''} × ${price.quantity}`, `${fmt(price.preVat)} THB`)}
      {price.vat > 0 && row(`VAT ${price.vatRate}%`, `${fmt(price.vat)} THB`)}
      {row(t('wf_total'), `${fmt(price.total)} THB`, !(price.wht > 0))}
      {price.wht > 0 && row(`WHT ${price.whtRate}%`, `− ${fmt(price.wht)} THB`)}
      {price.wht > 0 && row(t('wf_net'), `${fmt(price.net)} THB`, true)}
    </div>
  );
}

// ── Client-page preview (no instance created) ────────────────────────────────
// Renders the REAL share-page component so what staff see is what the client gets. Example images
// live behind the authed file route, so they are fetched as blobs and swapped into the payload.
function PreviewOverlay({ serviceId, steps, onClose }: { serviceId: string; steps?: any[]; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState('');
  const [blobs, setBlobs] = useState<Record<string, string>>({});
  useEffect(() => {
    let dead = false;
    const urls: string[] = [];
    api.wfFlowPreview(serviceId, steps).then(async (d: any) => {
      if (dead) return;
      setData(d);
      const ids: string[] = (d.steps || []).flatMap((st: any) => (st.files || []).map((f: any) => f.id));
      const map: Record<string, string> = {};
      for (const id of ids) { try { const u = await api.wfFileBlobUrl(id); map[id] = u; urls.push(u); } catch { /* skip */ } }
      if (!dead) setBlobs(map);
    }).catch((e: any) => !dead && setErr(e.message));
    return () => { dead = true; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [serviceId]);   // eslint-disable-line
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10005, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 0' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 940, maxWidth: '96vw', maxHeight: '94vh', overflowY: 'auto', borderRadius: 14, background: '#fff', boxShadow: 'var(--shadow-md)' }}>
        {err ? <div style={{ padding: 40, textAlign: 'center', color: '#E24B4A', fontSize: 13 }}>{err}</div>
          : !data ? <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-tertiary)' }}><Spin /></div>
            : <WfShare preview={{ data, fileUrl: (id: string) => blobs[id] || '', onClose }} />}
      </div>
    </div>
  );
}

// ── Create wizard ─────────────────────────────────────────────────────────────
// `desk` (2026-09-23): the wizard offers ONLY that desk's live services, grouped by family, across
// every firm of the world (no firm chips). `quiet` opens the matter muted — the engine sends nothing
// for it, at creation or later (owner decision 5: 暂时不通知).
function CreateWizard({ onClose, onCreated, desk = '', families = [], quiet = false }: { onClose: () => void; onCreated: (id: string) => void; desk?: string; families?: { key: string; label: string }[]; quiet?: boolean }) {
  const { t } = useTranslation();
  const [clientQ, setClientQ] = useState('');
  const [clients, setClients] = useState<any[]>([]);
  const [client, setClient] = useState<any>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [services, setServices] = useState<any[]>([]);
  const [svcQ, setSvcQ] = useState('');
  const [service, setService] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [unitPrice, setUnitPrice] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (desk) {
      const order = new Map(families.map((f, i) => [f.key, i]));
      api.getServices().then((d: any) => setServices(asRows(d)
        // Not offered when the catalogue says this service is not run as a workflow (services.has_flow,
        // owner 2026-09-25) — a one-off document or a postage charge has no steps to watch.
        .filter((s: any) => String(s.desk || '') === desk && Number(s.active) !== 0 && Number(s.has_flow ?? 1) !== 0)
        .sort((a: any, b: any) => ((order.get(a.family) ?? 99) - (order.get(b.family) ?? 99)) || String(a.name).localeCompare(String(b.name))))).catch(() => {});
      return;
    }
    api.getServiceCompanies().then((d: any) => { const rows = firmsWithServices(d); setCompanies(rows); if (rows[0]) setCompanyId(rows[0].id); }).catch(() => {});
  }, []);   // eslint-disable-line
  useEffect(() => { const h = setTimeout(() => { api.getClients({ q: clientQ, all: true }).then((d: any) => setClients(asRows(d).slice(0, 30))).catch(() => {}); }, 250); return () => clearTimeout(h); }, [clientQ]);
  // Inactive services are not startable (POST /instances refuses them), so the picker omits them.
  useEffect(() => { if (desk) return; if (companyId) api.getServices(companyId).then((d: any) => setServices(asRows(d).filter((s: any) => Number(s.active) !== 0 && Number(s.has_flow ?? 1) !== 0))).catch(() => {}); setService(null); }, [companyId]);   // eslint-disable-line
  useEffect(() => {
    setPreview(null);
    if (client && service) {
      api.wfPricePreview(client.id, service.id).then((p: any) => { setPreview(p); setUnitPrice(String(p.unitPrice)); setQuantity(String(p.quantity)); setTitle(p.serviceName || service.name); }).catch((e: any) => setErr(e.message));
    }
  }, [client, service]);   // eslint-disable-line

  const create = async () => {
    if (!client || !service || busy) return;
    setBusy(true); setErr('');
    try {
      const r: any = await api.wfInstanceCreate({ clientId: client.id, serviceId: service.id, title, unitPrice: Number(unitPrice) || 0, quantity: Number(quantity) || 1, ...(quiet ? { notify: false, clientNotify: false } : {}) });
      onCreated(r.id); onClose();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 640, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22 }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 16 }}>{t('wf_new')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>1 · {t('wf_pick_client')}</label>
            {client ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', border: `1px solid ${G}`, borderRadius: 9, background: '#F0FDF9' }}>
                <i className="ti ti-user" style={{ color: G }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{client.company_name || client.client_name}</div>
                <button onClick={() => setClient(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
              </div>
            ) : (<>
              <input value={clientQ} onChange={e => setClientQ(e.target.value)} placeholder={t('wf_search_client')} style={inp} autoFocus />
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 9, marginTop: 6 }}>
                {clients.map(c => (
                  <div key={c.id} onClick={() => setClient(c)} style={{ padding: '7px 11px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <b>{c.company_name || c.client_name}</b>{c.email ? <span style={{ color: 'var(--text-tertiary)', marginLeft: 8, fontSize: 11.5 }}>{c.email}</span> : null}
                  </div>
                ))}
                {!clients.length && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('wf_no_match')}</div>}
              </div>
            </>)}
          </div>
          <div>
            <label style={lbl}>2 · {t('wf_pick_service')}</label>
            {!desk && <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              {companies.map((c: any) => (
                <button key={c.id} onClick={() => setCompanyId(c.id)} style={{ ...ddl, background: companyId === c.id ? G : 'var(--bg-surface)', color: companyId === c.id ? '#fff' : G, border: `1px solid ${G}` }}>{String(c.name || '').replace('SLF ', '')}</button>
              ))}
            </div>}
            {service ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', border: `1px solid ${G}`, borderRadius: 9, background: '#F0FDF9' }}>
                <i className="ti ti-package" style={{ color: G }} />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{service.name}</div>
                <button onClick={() => setService(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-x" /></button>
              </div>
            ) : (<>
              <input value={svcQ} onChange={e => setSvcQ(e.target.value)} placeholder={t('wf_search_service')} style={inp} />
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 9, marginTop: 6 }}>
                {services.filter(s => !svcQ || String(s.name).toLowerCase().includes(svcQ.toLowerCase())).slice(0, 40).map(s => (
                  <div key={s.id} onClick={() => setService(s)} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 11px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <span><b>{s.name}</b>{desk && <span style={{ color: 'var(--text-tertiary)', marginLeft: 8, fontSize: 11.5 }}>{families.find(f => f.key === s.family)?.label || ''}</span>}</span><span style={{ color: 'var(--text-tertiary)' }}>{Number(s.price) > 0 ? `${fmt(s.price)} THB` : '—'}</span>
                  </div>
                ))}
              </div>
            </>)}
          </div>
          {client && service && (
            <div>
              <label style={lbl}>3 · {t('wf_confirm_price')}</label>
              {!preview ? <Spin /> : (
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div><label style={lbl}>{t('wf_title')}</label><input value={title} onChange={e => setTitle(e.target.value)} style={inp} /></div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}><label style={lbl}>{t('wf_unit_price')}</label><input type="number" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} style={inp} /></div>
                      <div style={{ width: 90 }}><label style={lbl}>{t('wf_qty')}</label><input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} style={inp} /></div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{preview.source === 'client_service' ? t('wf_price_from_cs') : t('wf_price_from_catalog')}</div>
                  </div>
                  <PriceCard price={{ ...preview, unitPrice: Number(unitPrice) || 0, quantity: Number(quantity) || 1, preVat: (Number(unitPrice) || 0) * (Number(quantity) || 1), vat: preview.vatRate > 0 ? (Number(unitPrice) || 0) * (Number(quantity) || 1) * preview.vatRate / 100 : 0, wht: (Number(unitPrice) || 0) * (Number(quantity) || 1) * (preview.whtRate || 0) / 100, total: (Number(unitPrice) || 0) * (Number(quantity) || 1) * (1 + (preview.vatRate || 0) / 100), net: (Number(unitPrice) || 0) * (Number(quantity) || 1) * (1 + (preview.vatRate || 0) / 100) - (Number(unitPrice) || 0) * (Number(quantity) || 1) * (preview.whtRate || 0) / 100 }} t={t} />
                </div>
              )}
            </div>
          )}
          {err && <div style={{ fontSize: 12, color: '#E24B4A' }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ ...ddl, fontWeight: 700 }}>{t('cancel')}</button>
          <button onClick={create} disabled={!client || !service || !preview || busy} style={{ ...btn, opacity: !client || !service || !preview || busy ? 0.6 : 1 }}>{busy ? <Spin /> : t('wf_create')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Flow editor (template steps of one service, or the global default) ───────
function FlowEditor({ serviceId, serviceName, onBack }: { serviceId: string; serviceName: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [steps, setSteps] = useState<any[]>([]);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const [imgBusy, setImgBusy] = useState('');
  const [preview, setPreview] = useState(false);
  const imgInput = useRef<HTMLInputElement>(null);
  const imgTarget = useRef<string>('');

  const load = () => { setLoading(true); api.wfFlow(serviceId).then((d: any) => { setSteps(d.steps || []); setIsDefault(!!d.isDefault); setDirty(false); }).catch((e: any) => setErr(e.message)).finally(() => setLoading(false)); };
  useEffect(load, [serviceId]);   // eslint-disable-line

  const set = (i: number, patch: any) => { setSteps(p => p.map((s, j) => (j === i ? { ...s, ...patch } : s))); setDirty(true); };
  const move = (i: number, d: number) => { setSteps(p => { const a = [...p]; const j = i + d; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return a; }); setDirty(true); };
  const remove = (i: number) => { setSteps(p => p.filter((_, j) => j !== i)); setDirty(true); };
  // The firm's standing pattern (owner-set 2026-07-31):
  //   · step 1 (Quotation / Invoice) gates everything — nothing else opens until it is complete;
  //   · every other step ALSO waits on the step directly above it, so the flow stays sequential;
  //   · the LAST step (client confirmation) waits on every step before it.
  // A new / inserted step inherits that pattern automatically.
  const stdDeps = (all: any[], idx: number): string[] => {
    if (idx <= 0) return [];
    if (idx === all.length - 1) return all.slice(0, idx).map(y => y.id).filter(Boolean);
    return Array.from(new Set([all[0]?.id, all[idx - 1]?.id].filter(Boolean))) as string[];
  };
  const blank = (prevId?: string): any => ({
    id: '', title: '', detail: '', parties: ['slf'], uploads: [], confirm_label: '',
    deps: Array.from(new Set([steps[0]?.id, prevId].filter(Boolean))), notify: 'none', notify_tg: 'none', images: [],
  });
  const add = () => { setSteps(p => [...p, blank(p[p.length - 1]?.id)]); setDirty(true); };
  const insertAfter = (i: number) => { setSteps(p => { const a = [...p]; a.splice(i + 1, 0, blank(p[i]?.id)); return a; }); setDirty(true); };
  const applyStandardLinks = () => {
    setSteps(p => {
      if (p.some(x => !x.id)) { alert(t('wf_links_save_first')); return p; }
      return p.map((x, i) => ({ ...x, deps: stdDeps(p, i) }));
    });
    setDirty(true);
  };

  const save = async () => {
    if (saving) return; setSaving(true); setErr('');
    try {
      await api.wfFlowSave({ serviceId, steps: steps.map(s => ({ id: s.id, title: s.title, detail: s.detail, parties: s.parties, uploads: s.uploads, confirmLabel: s.confirm_label, dep: s.deps || [], notify: s.notify || 'none', notifyTg: s.notify_tg || 'none' })) });
      load();
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const revert = async () => { if (!window.confirm(t('wf_revert_confirm'))) return; await api.wfFlowRevert(serviceId); load(); };
  const pickImage = (stepId: string) => { imgTarget.current = stepId; imgInput.current?.click(); };
  const onImage = async (e: any) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f || !imgTarget.current) return;
    const sid = imgTarget.current;
    setImgBusy(sid);
    try { await api.wfFlowStepImage(sid, f); load(); } catch (er: any) { alert(er.message); } finally { setImgBusy(''); }
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <input ref={imgInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={onImage} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={onBack} style={{ ...ddl, padding: '6px 10px' }}><i className="ti ti-arrow-left" /></button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{serviceId ? serviceName : t('wf_default_flow')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{serviceId ? (isDefault ? t('wf_using_default') : t('wf_customized')) : t('wf_default_hint')}</div>
        </div>
        {serviceId && !isDefault && <button onClick={revert} style={{ ...ddl, color: '#B45309' }}><i className="ti ti-restore" /> {t('wf_revert')}</button>}
        <button onClick={applyStandardLinks} style={ddl} title={t('wf_links_hint')}><i className="ti ti-hierarchy-2" /> {t('wf_links_std')}</button>
        <button onClick={() => setPreview(true)} style={{ ...ddl, color: G, borderColor: G }} title={t('wf_preview_hint')}><i className="ti ti-eye" /> {t('wf_preview')}</button>
        <button onClick={save} disabled={saving || !dirty} style={{ ...btn, opacity: saving || !dirty ? 0.6 : 1 }}>{saving ? <Spin /> : t('save')}</button>
      </div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', marginBottom: 10 }}>{err}</div>}
      {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><Spin /></div> : (<>
        {steps.map((s, i) => (
          <div key={s.id || `new-${i}`} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 10, background: 'var(--bg-surface)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: G, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
              <input value={s.title} onChange={e => set(i, { title: e.target.value })} placeholder={t('wf_step_title')} style={{ ...inp, fontWeight: 700 }} />
              <button onClick={() => move(i, -1)} disabled={i === 0} style={{ ...ddl, padding: '5px 8px', opacity: i === 0 ? 0.4 : 1 }}><i className="ti ti-arrow-up" /></button>
              <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} style={{ ...ddl, padding: '5px 8px', opacity: i === steps.length - 1 ? 0.4 : 1 }}><i className="ti ti-arrow-down" /></button>
              <button onClick={() => remove(i)} style={{ ...ddl, padding: '5px 8px', color: '#DC2626' }}><i className="ti ti-trash" /></button>
            </div>
            <textarea value={s.detail} onChange={e => set(i, { detail: e.target.value })} placeholder={t('wf_step_detail')} rows={2} style={{ ...inp, resize: 'vertical', marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <label style={lbl}>{t('wf_responsible')}</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['client', 'agent', 'slf'].map(p => (
                    <label key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                      <input type="checkbox" checked={(s.parties || []).includes(p)} onChange={e => set(i, { parties: e.target.checked ? [...(s.parties || []), p] : (s.parties || []).filter((x: string) => x !== p) })} />
                      {PARTY_META[p].label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ minWidth: 250 }}>
                <label style={lbl}>{t('wf_dep')}</label>
                <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, padding: '6px 8px', maxHeight: 118, overflowY: 'auto', background: 'var(--bg-surface)' }}>
                  {steps.filter((_, j) => j !== i).length === 0
                    ? <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('wf_dep_none')}</div>
                    : steps.map((x, j) => j === i ? null : (
                      <label key={x.id || j} title={x.title} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '2px 0', cursor: x.id ? 'pointer' : 'default', opacity: x.id ? 1 : 0.5 }}>
                        <input type="checkbox" disabled={!x.id} checked={(s.deps || []).includes(x.id)}
                          onChange={e => set(i, { deps: e.target.checked ? [...(s.deps || []), x.id] : (s.deps || []).filter((d: string) => d !== x.id) })} />
                        <span style={{ fontWeight: 700, color: 'var(--text-tertiary)' }}>{j + 1}.</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.title || t('wf_step_title')}</span>
                      </label>
                    ))}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3 }}>
                  {(s.deps || []).length ? t('wf_dep_count', { n: (s.deps || []).length }) : t('wf_dep_none_hint')}
                </div>
              </div>
              <div>
                <label style={lbl}><i className="ti ti-mail" /> {t('wf_notify')}</label>
                <select value={s.notify || 'none'} onChange={e => set(i, { notify: e.target.value })} style={{ ...ddl, fontWeight: 500 }}>
                  <option value="none">{t('wf_notify_none')}</option>
                  <option value="internal">{t('wf_notify_internal')}</option>
                  <option value="client">{t('wf_notify_client')}</option>
                  <option value="both">{t('wf_notify_both')}</option>
                </select>
              </div>
              <div>
                <label style={lbl}><i className="ti ti-brand-telegram" /> {t('wf_notify_tg')}</label>
                <select value={s.notify_tg || 'none'} onChange={e => set(i, { notify_tg: e.target.value })} style={{ ...ddl, fontWeight: 500 }}>
                  <option value="none">{t('wf_notify_none')}</option>
                  <option value="admin">{t('wf_tg_admin')}</option>
                  <option value="client">{t('wf_notify_client')}</option>
                  <option value="both">{t('wf_tg_both')}</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={lbl}>
                  {t('wf_uploads')}
                  {/* A long list needs its own count: past a dozen rows "did my last one save?" is not
                      answerable by eye, and the count is also where the ceiling becomes visible. */}
                  {(s.uploads || []).length > 0 && (
                    <span style={{ marginLeft: 6, fontWeight: 600, color: 'var(--text-tertiary)' }}>{(s.uploads || []).length} / {MAX_SLOTS}</span>
                  )}
                </label>
                {(s.uploads || []).map((u: any, k: number) => (
                  <div key={k} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'center' }}>
                    <span style={{ width: 20, textAlign: 'right', flexShrink: 0, fontSize: 11, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{k + 1}</span>
                    <input value={u.label} onChange={e => set(i, { uploads: s.uploads.map((x: any, m: number) => (m === k ? { ...x, label: e.target.value, key: x.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40) } : x)) })} style={{ ...inp, padding: '6px 9px' }} />
                    <button onClick={() => set(i, { uploads: s.uploads.filter((_: any, m: number) => m !== k) })} style={{ ...ddl, padding: '4px 8px', color: '#DC2626' }}><i className="ti ti-x" /></button>
                  </div>
                ))}
                <button
                  onClick={() => set(i, { uploads: [...(s.uploads || []), { key: nextSlotKey(s.uploads || []), label: '' }] })}
                  disabled={(s.uploads || []).length >= MAX_SLOTS}
                  title={(s.uploads || []).length >= MAX_SLOTS ? `Maximum ${MAX_SLOTS} upload slots per step` : undefined}
                  style={{ ...ddl, fontSize: 12, marginLeft: 26, opacity: (s.uploads || []).length >= MAX_SLOTS ? 0.45 : 1, cursor: (s.uploads || []).length >= MAX_SLOTS ? 'default' : 'pointer' }}>
                  <i className="ti ti-plus" /> {t('wf_add_upload')}
                </button>
              </div>
            </div>
            {/* Status is never configured — it is driven by what happens on the step. Spelling the rule
                out here is the difference between staff trusting the flow and staff guessing at it. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => insertAfter(i)} style={{ ...ddl, fontSize: 11.5, padding: '3px 10px', color: G, borderColor: G }}>
                <i className="ti ti-corner-left-down" /> {t('wf_insert_below')}
              </button>
            </div>
            <div style={{ marginTop: 10, padding: '7px 10px', background: 'var(--c-gray-50)', borderRadius: 8, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
              <b style={{ color: 'var(--text-primary)' }}>{t('wf_status_rule')}</b>{' '}
              <span style={{ ...chipS, background: '#F3F4F6', color: '#6B7280' }}>{t('wf_st_waiting')}</span>
              {' → '}
              <span style={{ ...chipS, background: '#FEF9C3', color: '#A16207' }}>{t('wf_st_in_progress')}</span>{' '}
              {(s.uploads || []).length || s.confirm_label
                ? t((s.parties || []).includes('client') ? 'wf_rule_auto_client' : 'wf_rule_auto_slf')
                : t('wf_rule_manual')}
              {' → '}
              <span style={{ ...chipS, background: '#ECFDF5', color: '#15803D' }}>{t('wf_st_complete')}</span>{' '}
              {t('wf_rule_complete')}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={lbl}>{t('wf_confirm_label')}</label>
                <input value={s.confirm_label} onChange={e => set(i, { confirm_label: e.target.value })} placeholder={t('wf_confirm_ph')} style={inp} />
              </div>
              <div>
                <label style={lbl}>{t('wf_example_img')}</label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {(s.images || []).map((img: any) => (
                    <span key={img.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, border: '1px solid var(--border)', borderRadius: 6, padding: '3px 7px' }}>
                      <i className="ti ti-photo" style={{ color: G }} />{img.filename}
                      <button onClick={async () => { await api.wfFileDelete(img.id); load(); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0 }}><i className="ti ti-x" /></button>
                    </span>
                  ))}
                  <button onClick={() => s.id ? pickImage(s.id) : alert(t('wf_save_first'))} disabled={imgBusy === s.id} style={{ ...ddl, fontSize: 12, opacity: imgBusy === s.id ? 0.6 : 1 }}>
                    {imgBusy === s.id ? <Spin /> : <><i className="ti ti-photo-plus" /> {t('wf_upload_img')}</>}</button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} style={{ ...ddl, width: '100%', padding: 12, borderStyle: 'dashed', color: G, fontWeight: 700 }}><i className="ti ti-plus" /> {t('wf_add_step')}</button>
      </>)}
      {preview && (
        <PreviewOverlay serviceId={serviceId}
          steps={steps.map(x => ({ id: x.id, title: x.title, detail: x.detail, parties: x.parties, uploads: x.uploads, confirmLabel: x.confirm_label, dep: x.deps || [] }))}
          onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

// ── Flows tab (service picker) ───────────────────────────────────────────────
function FlowsTab({ initialServiceId = '', onOpened }: { initialServiceId?: string; onOpened?: () => void }) {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [services, setServices] = useState<any[]>([]);
  const [custom, setCustom] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    api.getServiceCompanies().then((d: any) => { const rows = firmsWithServices(d); setCompanies(rows); if (rows[0]) setCompanyId(rows[0].id); }).catch(() => {});
    api.wfFlowCustomized().then((d: any) => setCustom(new Set(d.serviceIds || []))).catch(() => {});
  }, []);
  useEffect(() => { if (companyId) api.getServices(companyId).then((d: any) => setServices(asRows(d))).catch(() => {}); }, [companyId]);
  // Deep link from Services → "Workflow" button: open that service's editor immediately. The service
  // may belong to the other firm, so resolve its name from whichever catalogue answers first.
  useEffect(() => {
    if (!initialServiceId || open) return;
    const hit = services.find((x: any) => x.id === initialServiceId);
    if (hit) { setOpen({ id: hit.id, name: hit.name }); onOpened?.(); return; }
    if (!companies.length) return;
    (async () => {
      for (const c of companies) {
        try {
          const rows = asRows(await api.getServices(c.id));
          const m = rows.find((x: any) => x.id === initialServiceId);
          if (m) { setCompanyId(c.id); setOpen({ id: m.id, name: m.name }); onOpened?.(); return; }
        } catch { /* try next firm */ }
      }
      onOpened?.();   // unknown id — fall back to the plain list rather than looping
    })();
  }, [initialServiceId, services, companies]);   // eslint-disable-line

  // Every non-deleted service of the firm is listed (the catalogue endpoint applies no `active`
  // filter), so these numbers match the counts in the Services module.
  const shown = services.filter(s => !q || String(s.name).toLowerCase().includes(q.toLowerCase()));

  if (open) return <FlowEditor serviceId={open.id} serviceName={open.name} onBack={() => { setOpen(null); api.wfFlowCustomized().then((d: any) => setCustom(new Set(d.serviceIds || []))).catch(() => {}); }} />;

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {companies.map((c: any) => (
          <button key={c.id} onClick={() => setCompanyId(c.id)} style={{ ...ddl, background: companyId === c.id ? G : 'var(--bg-surface)', color: companyId === c.id ? '#fff' : G, border: `1px solid ${G}` }}>{String(c.name || '').replace('SLF ', '')}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('wf_search_service')} style={{ ...ddl, fontWeight: 400, width: 200, cursor: 'text' }} />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', alignSelf: 'center' }}>
          {t('wf_svc_count', { n: shown.length, total: services.length })}
        </span>
      </div>
      <div onClick={() => setOpen({ id: '', name: '' })} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1.5px solid ${G}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10, cursor: 'pointer', background: '#F0FDF9' }}>
        <i className="ti ti-route" style={{ color: G, fontSize: 18 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>{t('wf_default_flow')}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('wf_default_desc')}</div>
        </div>
        <i className="ti ti-chevron-right" style={{ color: 'var(--text-tertiary)' }} />
      </div>
      {shown.map((s, i) => (
        <div key={s.id} onClick={() => setOpen({ id: s.id, name: s.name })} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 8, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
          <span style={{ width: 26, flexShrink: 0, fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textAlign: 'right' }}>{i + 1}</span>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
            {s.name}
            {Number(s.active) === 0 && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'var(--c-gray-50)', color: 'var(--text-tertiary)' }}>{t('wf_svc_inactive')}</span>}
          </div>
          {custom.has(s.id)
            ? <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#EDE9FE', color: '#6D28D9' }}>{t('wf_customized')}</span>
            : <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--c-gray-50)', color: 'var(--text-tertiary)' }}>{t('wf_using_default')}</span>}
          <i className="ti ti-chevron-right" style={{ color: 'var(--text-tertiary)' }} />
        </div>
      ))}
    </div>
  );
}

// ── Instance detail ──────────────────────────────────────────────────────────
// The endpoints the panel talks to. Default = the staff /api/wf routes (workflows module).
// The Jobs panel injects the /api/jobs-wf mirrors instead — job operators (Joy/May) don't hold the
// 'workflows' module, and the job-scoped routes only ever reach the job's own linked instance.
// Optional members hide their controls (price edit / regen / cancel stay Workflows-module-only).
export type WfPanelApi = {
  get: () => Promise<any>;
  stepPatch: (stepId: string, data: any) => Promise<any>;
  stepComment: (stepId: string, body: string) => Promise<any>;
  stepUpload: (stepId: string, file: File, slot: string) => Promise<any>;
  fileOpen: (fileId: string, filename: string) => Promise<any>;
  fileDelete: (fileId: string) => Promise<any>;
  instancePatch?: (data: any) => Promise<any>;
  regenToken?: () => Promise<any>;
};
const defaultEp = (id: string): WfPanelApi => ({
  get: () => api.wfInstanceGet(id),
  stepPatch: (stepId, data) => api.wfStepPatch(stepId, data),
  stepComment: (stepId, body) => api.wfStepComment(stepId, body),
  stepUpload: (stepId, file, slot) => api.wfStepUpload(stepId, file, slot),
  fileOpen: (fileId, filename) => api.wfFileOpen(fileId, filename),
  fileDelete: (fileId) => api.wfFileDelete(fileId),
  instancePatch: (data) => api.wfInstancePatch(id, data),
  regenToken: () => api.wfRegenToken(id),
});

// A desk-only login works the steps but does not manage the matter (no cancel / re-price / new link):
// the same optional-member mechanism the Jobs panel uses. The server refuses those calls anyway.
const workOnlyEp = (id: string): WfPanelApi => { const e = defaultEp(id); delete e.instancePatch; delete e.regenToken; return e; };
// The endpoint set a desk console hands the detail panel: work-only unless the login manages, and —
// under a quiet desk — every status change goes out `silent`, so a matter that was NOT opened muted
// (job-linked, pre-existing) still notifies nobody from here (owner decision 5, 2026-09-23).
const deskEp = (id: string, manage: boolean, quiet: boolean): WfPanelApi => {
  const e = manage ? defaultEp(id) : workOnlyEp(id);
  if (quiet) { const sp = e.stepPatch; e.stepPatch = (stepId, data) => sp(stepId, data && data.status != null ? { ...data, silent: true } : data); }
  return e;
};

export function InstanceDetail({ id, onBack, ep, compact = false, quiet = false }: { id: string; onBack: () => void; ep?: WfPanelApi; compact?: boolean; quiet?: boolean }) {
  const { t } = useTranslation();
  // Stable across renders — rebuilding the endpoint set each render would be harmless but noisy.
  const epRef = useRef<WfPanelApi | null>(null);
  const epKey = useRef('');
  if (epKey.current !== id) { epKey.current = id; epRef.current = ep || defaultEp(id); }
  else if (ep) epRef.current = ep;
  const E = epRef.current!;
  const [inst, setInst] = useState<any>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busyStep, setBusyStep] = useState('');
  const [uploading, setUploading] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const fileTarget = useRef<{ stepId: string; slot: string }>({ stepId: '', slot: '' });
  const [editPrice, setEditPrice] = useState(false);
  const [up, setUp] = useState(''); const [qty, setQty] = useState('');
  // ── Which steps are open (owner 2026-09-25: 更清晰、更方便使用) ─────────────────────────────
  // A matter here runs 12 to 15 steps and EVERY one of them was drawn open — title, description,
  // an upload area and a comment box each — so the page was fifteen identical boxes and the one
  // step anybody was actually working on looked like all the others. Steps collapse to a line now;
  // the line still carries the party badges, the status control and how many files and comments are
  // inside, so nothing is hidden, only folded.
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());
  const autoFor = useRef('');
  const toggleStep = (sid: string) => setOpenSteps(p => { const n = new Set(p); if (n.has(sid)) n.delete(sid); else n.add(sid); return n; });
  const [menu, setMenu] = useState(false);

  const load = () => E.get().then((d: any) => { setInst(d); setLoadErr(''); }).catch((e: any) => setLoadErr(e.message || 'Failed to load'));
  useEffect(() => { load(); }, [id]);   // eslint-disable-line
  // Opens on the step being worked — the first one not finished — and only once per matter, so a
  // reload after every click does not reopen what the operator just folded away.
  useEffect(() => {
    if (!inst || autoFor.current === inst.id) return;
    autoFor.current = inst.id;
    const cur = (inst.steps || []).find((x: any) => x.status !== 'complete');
    setOpenSteps(new Set(cur ? [cur.id] : []));
  }, [inst?.id]);   // eslint-disable-line

  // A teammate deleting the workflow used to leave an eternal spinner with no way back.
  if (loadErr) return (
    <div style={{ padding: 40, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <i className="ti ti-alert-triangle" style={{ fontSize: 24, color: '#B45309' }} />
      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{loadErr}</div>
      <button onClick={onBack} style={{ ...ddl, fontWeight: 700 }}><i className="ti ti-arrow-left" /> {t('cancel')}</button>
    </div>
  );
  if (!inst) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><Spin /></div>;
  const url = shareUrl(inst.client_token);
  const copy = () => { navigator.clipboard.writeText(url); alert(t('wf_link_copied')); };
  const regen = async () => { if (!E.regenToken || !window.confirm(t('wf_regen_confirm'))) return; await E.regenToken(); load(); };
  const setStatus = async (stepId: string, status: string) => {
    setBusyStep(stepId);
    try {
      await E.stepPatch(stepId, { status });
      // Finishing a step hands you the next one: fold this away, open what is now current.
      if (status === 'complete') {
        const list = (inst?.steps || []);
        const at = list.findIndex((x: any) => x.id === stepId);
        const next = list.slice(at + 1).find((x: any) => x.status !== 'complete');
        setOpenSteps(p => { const n = new Set(p); n.delete(stepId); if (next) n.add(next.id); return n; });
      }
      await load();
    } catch (e: any) { alert(e.message); } finally { setBusyStep(''); }
  };
  const comment = async (stepId: string) => {
    const body = (note[stepId] || '').trim();
    if (!body || busyStep === `cm-${stepId}`) return;
    setBusyStep(`cm-${stepId}`);
    try { await E.stepComment(stepId, body); setNote(p => ({ ...p, [stepId]: '' })); await load(); }
    catch (e: any) { alert(e.message); } finally { setBusyStep(''); }
  };
  const pickFile = (stepId: string, slot: string) => { fileTarget.current = { stepId, slot }; fileInput.current?.click(); };
  const onFile = async (e: any) => {
    const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
    const sid = fileTarget.current.stepId;
    setUploading(sid);
    try { await E.stepUpload(sid, f, fileTarget.current.slot); await load(); }
    catch (er: any) { alert(er.message); } finally { setUploading(''); }
  };
  const savePrice = async () => {
    if (savingPrice || !E.instancePatch) return; setSavingPrice(true);
    try { await E.instancePatch({ unitPrice: Number(up) || 0, quantity: Number(qty) || 1 }); setEditPrice(false); await load(); }
    catch (er: any) { alert(er.message); } finally { setSavingPrice(false); }
  };

  return (
    <div style={{ maxWidth: compact ? undefined : 900 }}>
      <input ref={fileInput} type="file" style={{ display: 'none' }} onChange={onFile} />
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
        {!compact && <button onClick={onBack} style={{ ...ddl, padding: '6px 10px' }}><i className="ti ti-arrow-left" /></button>}
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{inst.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
            {inst.client_name} · {String(inst.company_name || '').replace('SLF ', '')} · {inst.service_name || '—'}
          </div>
          {/* Where this matter has got to — the list says 3/15 and the page itself did not. */}
          {!!(inst.steps || []).length && (() => {
            const total = inst.steps.length;
            const done = inst.steps.filter((x: any) => x.status === 'complete').length;
            const all = done >= total;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 8, maxWidth: 340 }}>
                <div style={{ flex: 1, height: 7, borderRadius: 999, background: 'var(--c-gray-100)', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round(done * 100 / total)}%`, height: '100%', background: all ? '#15803D' : G, transition: 'width .2s' }} />
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', color: all ? '#15803D' : 'var(--text-secondary)' }}>{t('wf_steps_done', { done, total })}</span>
              </div>
            );
          })()}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: inst.status === 'complete' ? '#ECFDF5' : inst.status === 'cancelled' ? '#FEF2F2' : '#EFF6FF', color: inst.status === 'complete' ? '#15803D' : inst.status === 'cancelled' ? '#DC2626' : '#1D4ED8' }}>{t(`wf_st_${inst.status}`)}</span>
            {(quiet || Number(inst.client_notify ?? 1) === 0) && <span title={t('wf_notify_off_hint')} style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: '#FEF3C7', color: '#92400E' }}><i className="ti ti-bell-off" /> {t('wf_notify_off')}</span>}
            {/* The client link is one thing, so it is one control: copy it, or open it. Issuing a new
                link and cancelling the matter are rare and one of them is destructive — they sat in
                the same row, in the same weight, as the button you press every day. */}
            <button onClick={copy} style={{ ...ddl, fontSize: 12 }}><i className="ti ti-link" /> {t('wf_copy_link')}</button>
            <a href={url} target="_blank" rel="noreferrer" title={t('wf_open_link')} aria-label={t('wf_open_link')}
              style={{ ...ddl, fontSize: 12, padding: '6px 9px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', color: 'var(--text-secondary)' }}><i className="ti ti-external-link" /></a>
            {(E.regenToken || (E.instancePatch && inst.status !== 'cancelled')) && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setMenu(m => !m)} title={t('eml_nav_more')} aria-label={t('eml_nav_more')}
                  style={{ ...ddl, fontSize: 12, padding: '6px 9px', color: 'var(--text-secondary)' }}><i className="ti ti-dots" /></button>
                {menu && (<>
                  <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, minWidth: 210, background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', padding: 4 }}>
                    {E.regenToken && (
                      <button onClick={() => { setMenu(false); regen(); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#B45309', whiteSpace: 'nowrap' }}>
                        <i className="ti ti-refresh" />{t('wf_regen')}</button>
                    )}
                    {E.instancePatch && inst.status !== 'cancelled' && (
                      <button onClick={async () => { setMenu(false); if (window.confirm(t('wf_cancel_confirm'))) { await E.instancePatch!({ status: 'cancelled' }); load(); } }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#DC2626', whiteSpace: 'nowrap' }}>
                        <i className="ti ti-ban" />{t('wf_cancel_wf')}</button>
                    )}
                  </div>
                </>)}
              </div>
            )}
          </div>
        </div>
        {editPrice ? (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div><label style={lbl}>{t('wf_unit_price')}</label><input type="number" value={up} onChange={e => setUp(e.target.value)} style={{ ...inp, width: 120 }} /></div>
              <div><label style={lbl}>{t('wf_qty')}</label><input type="number" value={qty} onChange={e => setQty(e.target.value)} style={{ ...inp, width: 70 }} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditPrice(false)} style={{ ...ddl, fontSize: 12 }}>{t('cancel')}</button>
              <button onClick={savePrice} disabled={savingPrice} style={{ ...btn, fontSize: 12, padding: '6px 12px', opacity: savingPrice ? 0.6 : 1 }}>{savingPrice ? <Spin /> : t('save')}</button>
            </div>
          </div>
        ) : <PriceCard price={inst.price} t={t} onEdit={E.instancePatch ? () => { setUp(String(inst.price.unitPrice)); setQty(String(inst.price.quantity)); setEditPrice(true); } : undefined} />}
      </div>

      {(inst.steps || []).map((s: any, i: number) => {
        const isOpen = openSteps.has(s.id);
        const nFiles = (s.files || []).length, nComm = (s.comments || []).length, nConf = (s.confirmations || []).length;
        const stDone = s.status === 'complete';
        return (
        <div key={s.id} style={{ border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8, background: 'var(--bg-surface)', opacity: s.blockedBy ? 0.75 : 1, overflow: 'hidden' }}>
          {/* The whole line opens the step; the status control and its own buttons do not. */}
          <div onClick={() => toggleStep(s.id)} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '11px 14px', cursor: 'pointer' }}>
            <span style={{ width: 26, height: 26, borderRadius: 999, background: stDone ? '#15803D' : G, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 800, flexShrink: 0 }}>{stDone ? <i className="ti ti-check" /> : i + 1}</span>
            <div style={{ flex: 1, minWidth: 160, fontSize: 13.5, fontWeight: 800, color: stDone ? 'var(--text-secondary)' : 'var(--text-primary)' }}>{s.title}</div>
            <PartyBadges parties={s.parties} />
            {s.blockedBy ? <span style={{ fontSize: 11, fontWeight: 700, color: '#B45309', whiteSpace: 'nowrap' }}><i className="ti ti-lock" /> {t('wf_blocked_by', { n: (s.blockedSteps || [s.blockedBy]).join(', ') })}</span> : null}
            {/* Folded away is not hidden: the line still says what is inside. */}
            {!isOpen && (nFiles > 0 || nComm > 0 || nConf > 0) && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                {nConf > 0 && <span style={{ color: '#0E7C66', fontWeight: 700 }}><i className="ti ti-shield-check" /> {nConf}</span>}
                {nFiles > 0 && <span><i className="ti ti-paperclip" /> {nFiles}</span>}
                {nComm > 0 && <span><i className="ti ti-message-circle" /> {nComm}</span>}
              </span>
            )}
            <div onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
              {(['waiting', 'in_progress', 'complete'] as const).map(st => (
                <button key={st} onClick={() => setStatus(s.id, st)} disabled={busyStep === s.id}
                  style={{ padding: '5px 10px', border: 'none', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', background: s.status === st ? (st === 'complete' ? '#15803D' : st === 'in_progress' ? '#A16207' : 'var(--text-tertiary)') : 'var(--bg-surface)', color: s.status === st ? '#fff' : 'var(--text-secondary)' }}>
                  {STATUS_META[st].l}
                </button>
              ))}
            </div>
            <i className={`ti ti-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: 14, color: 'var(--text-tertiary)' }} aria-hidden />
          </div>
          {isOpen && (
          <div style={{ padding: '0 14px 14px' }}>
          {s.detail && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 8, whiteSpace: 'pre-wrap' }}>{s.detail}</div>}
          {s.status_at ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{fmtTs(s.status_at)} · {s.status_by}</div> : null}
          <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={lbl}>{t('wf_files')} ({s.files.length})</span>
                <button onClick={() => pickFile(s.id, '')} disabled={uploading === s.id} style={{ ...ddl, fontSize: 11.5, padding: '3px 9px', opacity: uploading === s.id ? 0.6 : 1 }}>
                  {uploading === s.id ? <Spin /> : <><i className="ti ti-upload" /> {t('wf_upload')}</>}</button>
              </div>
              {/* Declared slots — staff fill these so the file lands in the right box on the client page
                  (an SLF step like "Quotation / Invoice Sent" is filled entirely from here). */}
              {(s.uploads || []).map((u: any) => {
                const inSlot = (s.files || []).filter((f: any) => f.slot === u.key);
                return (
                  <div key={u.key} style={{ border: '1px dashed var(--border-strong)', borderRadius: 8, padding: '7px 9px', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)' }}>{u.label}</span>
                      <button onClick={() => pickFile(s.id, u.key)} disabled={uploading === s.id}
                        style={{ ...ddl, fontSize: 11, padding: '2px 8px', marginLeft: 'auto', opacity: uploading === s.id ? 0.6 : 1 }}>
                        {uploading === s.id ? <Spin /> : <><i className="ti ti-upload" /> {t('wf_upload')}</>}</button>
                    </div>
                    {inSlot.map((f: any) => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, paddingTop: 5 }}>
                        <i className={/^image\//.test(f.mime) ? 'ti ti-photo' : 'ti ti-file'} style={{ color: G }} />
                        <button onClick={() => E.fileOpen(f.id, f.filename).catch((e: any) => alert(e.message))}
                          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, textAlign: 'left' }}>{f.filename}</button>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 10.5 }}>{f.uploader_party === 'client' ? t('wf_by_client') : 'SLF'}</span>
                        <button onClick={async () => { if (window.confirm(t('wf_del_file'))) { await E.fileDelete(f.id); load(); } }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-trash" /></button>
                      </div>
                    ))}
                  </div>
                );
              })}
              {(s.files || []).filter((f: any) => !(s.uploads || []).some((u: any) => u.key === f.slot)).map((f: any) => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, padding: '4px 0' }}>
                  <i className={/^image\//.test(f.mime) ? 'ti ti-photo' : 'ti ti-file'} style={{ color: G }} />
                  <button onClick={() => E.fileOpen(f.id, f.filename).catch((e: any) => alert(e.message))}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, textAlign: 'left' }}>{f.filename}</button>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 10.5 }}>{f.uploader_party === 'client' ? t('wf_by_client') : 'SLF'} · {Math.round((f.size || 0) / 1024)} KB</span>
                  <button onClick={async () => { if (window.confirm(t('wf_del_file'))) { await E.fileDelete(f.id); load(); } }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-trash" /></button>
                </div>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              {/* The client's confirmation, kept as evidence (owner 2026-08-24): exactly what they
                  agreed to, the IP they pressed it from and when. Append-only — the comment beside
                  it can be deleted, this cannot. */}
              {!!(s.confirmations || []).length && (
                <div style={{ marginBottom: 9, padding: '8px 10px', border: '1px solid #A7F3D0', borderRadius: 8, background: '#F0FDF4' }}>
                  {(s.confirmations || []).map((c: any) => (
                    <div key={c.id} style={{ fontSize: 11.5, lineHeight: 1.55 }}>
                      <div style={{ fontWeight: 700, color: '#0E7C66' }}>
                        <i className="ti ti-shield-check" style={{ marginRight: 4 }} />{t('wf_confirmed_by', { name: c.clientName })}
                      </div>
                      <div style={{ color: 'var(--text-secondary)' }}>“{c.label}”</div>
                      <div style={{ color: 'var(--text-tertiary)', fontFamily: 'ui-monospace, monospace', fontSize: 10.5 }}>
                        {fmtTs(c.at)} · IP {c.ip || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <span style={lbl}>{t('wf_comments')} ({s.comments.length})</span>
              {s.comments.map((c: any) => (
                <div key={c.id} style={{ borderLeft: `2px solid ${c.author_party === 'client' ? '#1D4ED8' : G}`, paddingLeft: 9, marginBottom: 7 }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}><b style={{ color: 'var(--text-secondary)' }}>{c.author_party === 'client' ? `${t('wf_client')} · ${c.author_name}` : c.author_name}</b> · {fmtTs(c.created_at)}</div>
                  <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'flex-end' }}>
                {/* A textarea, not an <input> (owner 2026-08-25): step comments are often checklists
                    — the DD document list on job 61,118 was one unbroken paragraph because the box
                    could not take a newline. Enter now breaks the line; Ctrl/Cmd+Enter posts (the
                    display side always rendered pre-wrap, so old comments are untouched). */}
                <textarea value={note[s.id] || ''} onChange={e => setNote(p => ({ ...p, [s.id]: e.target.value }))}
                  placeholder={t('wf_comment_ph')} rows={Math.min(6, Math.max(1, (note[s.id] || '').split('\n').length))}
                  title={t('wf_comment_nl_hint')}
                  style={{ ...inp, padding: '6px 9px', resize: 'vertical', minHeight: 32, lineHeight: 1.45, fontFamily: 'inherit' }}
                  onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) { e.preventDefault(); comment(s.id); } }} />
                <button onClick={() => comment(s.id)} disabled={busyStep === `cm-${s.id}`} title={t('wf_comment_nl_hint')} style={{ ...btn, padding: '6px 12px', fontSize: 12, opacity: busyStep === `cm-${s.id}` ? 0.6 : 1 }}>{busyStep === `cm-${s.id}` ? <Spin /> : t('wf_post')}</button>
              </div>
            </div>
          </div>
          </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

// ── Module root ──────────────────────────────────────────────────────────────
/**
 * `desk` turns this screen into one desk console: SLF Property's Legal Matters (desk 'property',
 * owner 2026-09-22) and SLF™ Visa / Company / License (2026-09-23). Same engine, same list, same
 * wizard — narrowed SERVER-SIDE to the desk's services and grouped by family, because Notary alone
 * is seven priced rows and Work Permit six; an ungrouped list would read as a price list. The
 * desks and their families are one table (server/src/services/serviceDesks.ts) read through
 * GET /wf/desks, which also says whether this login may manage (delete / cancel / re-price /
 * regenerate links) or only work the steps. Due Diligence, POA and Lease Registration are
 * deliberately NOT a family: each has its own tab and screens, untouched.
 *
 * The template editor (Service Flows) stays out of every desk — it edits the flow of EVERY service,
 * accounting ones included, so it belongs to the Workflows module and its permission.
 * Default '' is the standalone module, unchanged in every respect. `quiet` — see CreateWizard.
 */
export default function Workflows({ embedded = false, desk = '', quiet = false, famCtl, onFamilies }: {
  embedded?: boolean; desk?: string; quiet?: boolean;
  // A desk's page may put the family chips in its OWN top bar (SLF Property does — owner
  // 2026-09-25: Legal Matters 下的所有功能菜单都移到顶部). When it does, it owns the selection and
  // this component stops drawing a chip row of its own, so there is exactly one set on screen.
  famCtl?: { fam: string; setFam: (v: string) => void };
  onFamilies?: (families: { key: string; label: string; priced: boolean; used: number }[]) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { me } = useStore();
  const legal = !!desk;   // "scoped to a desk" — the name predates the three new desks
  const [famOwn, setFamOwn] = useState('');
  const fam = famCtl ? famCtl.fam : famOwn;
  const setFam = famCtl ? famCtl.setFam : setFamOwn;
  const [deskInfo, setDeskInfo] = useState<{ families: { key: string; label: string; priced: boolean; used: number }[]; canManage: boolean } | null>(null);
  useEffect(() => {
    if (!desk) return;
    api.wfDesks().then((d: any) => { const dk = (d.desks || []).find((x: any) => x.key === desk); setDeskInfo({ families: dk?.families || [], canManage: !!d.canManage }); }).catch(() => setDeskInfo({ families: [], canManage: false }));
  }, [desk]);
  useEffect(() => { if (deskInfo && onFamilies) onFamilies(deskInfo.families); }, [deskInfo]);   // eslint-disable-line
  const canManage = deskInfo ? deskInfo.canManage : (me?.role === 'admin' || (me?.modules || []).includes('workflows'));
  const deepFlow = legal ? '' : (sp.get('flow') || '');
  const deepPreview = legal ? '' : (sp.get('preview') || '');
  const [tab, setTab] = useState<'instances' | 'flows'>(!legal && (deepFlow || sp.get('v') === 'flows') ? 'flows' : 'instances');
  const [rows, setRows] = useState<any[]>([]);
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [detailId, setDetailId] = useState('');

  // A desk holds a handful of matters, so it fetches ALL statuses once and filters here — which is
  // what lets each status chip say how many are behind it. "Active 3 · Complete 8" is the thing a
  // desk is actually asked, and a count that only appears after you click the chip is no answer.
  // The standalone Workflows module keeps filtering in SQL: it is not desk-sized.
  const load = () => { setLoading(true); api.wfInstances({ status: legal ? '' : status, q, desk }).then((d: any) => setRows(d.rows || [])).catch(() => {}).finally(() => setLoading(false)); };
  // The desk is applied in SQL (the endpoint caps at 500 rows); the family chip filters here.
  const inFam = !legal ? rows : rows.filter(r => !fam || r.service_family === fam);
  const shown = !legal ? rows : inFam.filter(r => !status || r.status === status);
  const statusCount = (v: string) => (v ? inFam.filter(r => r.status === v).length : inFam.length);
  useEffect(() => { if (tab === 'instances' && !detailId) { const h = setTimeout(load, 200); return () => clearTimeout(h); } }, [legal ? '' : status, q, tab, detailId]);   // eslint-disable-line

  return (
    <div style={embedded ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-app)' } : { height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
          <button onClick={() => navigate('/')} title="SLF" style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}><img className="app-logo" src="/logo.png" alt="SLF" style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{t('mod_workflows')}</div>
        </header>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', flexWrap: 'nowrap', ...(legal ? { fontFamily: RC } : {}) }}>
        {legal ? (famCtl ? null : (
          // One chip per FAMILY, not per priced service — see server/src/services/serviceDesks.ts. A
          // family the catalogue has no live row for is shown greyed rather than hidden: the firm
          // offers the work, the price list simply has not caught up, and hiding it would bury that.
          // Two deliberate rows on a desk (2026-09-23): the family chips take the full width, and the
          // status chips + search + "New Workflow" sit on the line below — ten Visa families plus the
          // controls never fit one row, and letting them wrap left "+ New Workflow" alone on a third.
          !detailId && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
              <button onClick={() => setFam('')} style={{ ...ddl, fontWeight: 700, background: !fam ? G : 'var(--bg-surface)', color: !fam ? '#fff' : 'var(--text-secondary)', border: !fam ? 'none' : '1px solid var(--border-strong)' }}>{t('wf_all')}</button>
              {(deskInfo?.families || []).filter(f => f.used > 0 || f.key === fam).map(f => (
                <button key={f.key} onClick={() => setFam(f.key)} title={f.priced ? undefined : t('lm_not_priced')}
                  style={{ ...ddl, fontWeight: 700, background: fam === f.key ? G : 'var(--bg-surface)', color: fam === f.key ? '#fff' : (f.priced ? 'var(--text-secondary)' : 'var(--text-tertiary)'), border: fam === f.key ? 'none' : '1px solid var(--border-strong)' }}>
                  {f.label}{f.priced ? '' : ' ·'}
                </button>
              ))}
            </div>
          ))
        ) : (
          <div style={{ display: 'inline-flex', border: `1px solid ${G}`, borderRadius: 8, overflow: 'hidden' }}>
            {([['instances', t('wf_tab_instances')], ['flows', t('wf_tab_flows')]] as [any, string][]).map(([v, l]) => (
              <button key={v} onClick={() => { setTab(v); setDetailId(''); }} style={{ padding: '7px 14px', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', background: tab === v ? G : 'var(--bg-surface)', color: tab === v ? '#fff' : G }}>{l}</button>
            ))}
          </div>
        )}
        {tab === 'instances' && !detailId && (<>
          {/* One segmented control, not four loose pills. The families above are a CATEGORY and the
              statuses here are a STATE; drawn identically, the two rows read as one confused mass —
              which is what the desks looked like. Each segment carries its count. */}
          <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
            {[['', t('wf_all')], ['active', t('wf_st_active')], ['complete', t('wf_st_complete')], ['cancelled', t('wf_st_cancelled')]].map(([v, l], i) => (
              <button key={v} onClick={() => setStatus(v)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 13px', border: 'none', borderLeft: i ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', background: status === v ? G : 'var(--bg-surface)', color: status === v ? '#fff' : 'var(--text-secondary)', ...(legal ? { fontFamily: RC } : {}) }}>
                {l}{legal && <span style={{ fontSize: 11, fontWeight: 800, opacity: status === v ? 0.85 : 0.6 }}>{statusCount(v as string)}</span>}
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-tertiary)' }} aria-hidden />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('wf_search')} style={{ ...ddl, fontWeight: 400, width: '100%', paddingLeft: 29, boxSizing: 'border-box', cursor: 'text', ...(legal ? { fontFamily: RC } : {}) }} />
          </div>
        </>)}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px', minHeight: 0 }}>
        {tab === 'flows' ? <FlowsTab initialServiceId={deepFlow} onOpened={() => { if (deepFlow) { sp.delete('flow'); setSp(sp, { replace: true }); } }} /> : detailId ? <InstanceDetail id={detailId} onBack={() => setDetailId('')} quiet={quiet} ep={(canManage && !quiet) ? undefined : deskEp(detailId, canManage, quiet)} /> : (
          loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><Spin /></div>
            : shown.length === 0 ? (
                <div style={{ padding: '54px 20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  <i className="ti ti-folder-open" style={{ fontSize: 30, display: 'block', marginBottom: 8, opacity: 0.6 }} aria-hidden />
                  <div style={{ fontSize: 13 }}>{t('wf_none')}</div>
                  {tab === 'instances' && <div style={{ fontSize: 11.5, marginTop: 5 }}>{t('wf_none_hint')}</div>}
                </div>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                    <thead><tr>
                      {/* Client and matter are ONE column, two lines. They were two columns of mostly
                          empty width, which is what left the row looking scattered. */}
                      <th style={th}>{t('wf_col_client')}</th>
                      <th style={{ ...th, width: 130 }}>{t('wf_col_firm')}</th>
                      <th style={{ ...th, textAlign: 'right', width: 110 }}>{t('wf_total')}</th>
                      <th style={{ ...th, width: 150 }}>{t('wf_col_progress')}</th>
                      <th style={{ ...th, width: 96 }}>{t('wf_col_status')}</th>
                      <th style={{ ...th, width: 130 }}>{t('wf_col_updated')}</th><th style={{ ...th, width: 66 }} />
                    </tr></thead>
                    <tbody>
                      {shown.map(r => {
                        const pct = r.total_steps ? Math.round(r.done_steps * 100 / r.total_steps) : 0;
                        const done = r.total_steps > 0 && r.done_steps >= r.total_steps;
                        return (
                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDetailId(r.id)}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                          <td style={td}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{r.client_name}</div>
                            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 1 }}>{r.title}</div>
                          </td>
                          <td style={{ ...td, color: 'var(--text-tertiary)', fontSize: 12 }}>{String(r.company_name || '').replace('SLF ', '') || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.price?.total)}<span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', marginLeft: 4 }}>THB</span></td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 54, height: 7, borderRadius: 999, background: 'var(--c-gray-100)', overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: done ? '#15803D' : G, transition: 'width .2s' }} />
                              </div>
                              <span style={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: done ? '#15803D' : 'var(--text-secondary)' }}>{r.done_steps}/{r.total_steps}</span>
                            </div>
                          </td>
                          <td style={td}><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: r.status === 'complete' ? '#ECFDF5' : r.status === 'cancelled' ? '#FEF2F2' : '#EFF6FF', color: r.status === 'complete' ? '#15803D' : r.status === 'cancelled' ? '#DC2626' : '#1D4ED8' }}>{t(`wf_st_${r.status}`)}</span></td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{fmtTs(r.updated_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                            <button title={t('wf_copy_link')} onClick={() => { navigator.clipboard.writeText(shareUrl(r.client_token)); alert(t('wf_link_copied')); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: G, padding: 3 }}><i className="ti ti-link" style={{ fontSize: 15 }} /></button>
                            {canManage && <button title={t('wf_delete')} onClick={async () => { if (window.confirm(t('wf_delete_confirm'))) { await api.wfInstanceDelete(r.id); load(); } }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 3 }}><i className="ti ti-trash" style={{ fontSize: 15 }} /></button>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
        )}
      </div>
      {tab === 'instances' && !detailId && <Fab onClick={() => setWizard(true)} title={t('wf_new')} />}
      {wizard && <CreateWizard onClose={() => setWizard(false)} onCreated={id => setDetailId(id)} desk={desk} families={deskInfo?.families || []} quiet={quiet} />}
      {deepPreview && <PreviewOverlay serviceId={deepPreview} onClose={() => { sp.delete('preview'); setSp(sp, { replace: true }); }} />}
    </div>
  );
}
